/**
 * @file ContinueWatchingCard.tsx
 * @description Landscape card variant for Continue Watching rail
 *
 * Design specifications:
 * - 16:9 landscape aspect ratio (smaller than TitleCard)
 * - Progress bar overlay
 * - Metadata displayed below the card
 * - Hover reveals Play button overlay
 * - Info button in metadata section
 *
 * @architecture
 * Implements RAILS-01 Continue Watching rail display
 */

import type { ReactElement } from "react";
import type { ApiTitle } from "@lumi/data";
import { Button } from "@lumi/ui-headless";
import type { Variants } from "framer-motion";
import { Info, Play } from "lucide-react";
import { useCascadeFadeIn } from "../../hooks";
import { pageTransitions } from "../../transitions/pageTransitions";
import { NewBadge } from "../NewBadge";

interface ContinueWatchingCardProps {
  readonly title: ApiTitle;
  readonly index?: number;
  readonly className?: string;
  readonly progress: {
    assetId: string;
    progressPercent: number;
    positionSec: number;
    durationSec: number;
  };
  readonly transitionTo: (path: string, variant?: Variants) => void;
  readonly onAction?: () => void;
  readonly onInfo?: () => void;
}

export function ContinueWatchingCard({
  title,
  index = 0,
  progress,
  className = "",
  onAction,
  onInfo,
  transitionTo,
}: ContinueWatchingCardProps): ReactElement {
  const { ref, isVisible } = useCascadeFadeIn({ index });

  const handlePlay = () => {
    // Resume playback from saved position (global pointerdown listener exits nav mode)
    transitionTo(`/home/watch/${title.slug}?assetId=${progress.assetId}`, pageTransitions.cinematicFade);
  };

  const handleMoreInfo = () => {
    transitionTo(`/home/title/${title.slug}`, pageTransitions.fullScreenFade);
  };

  // Use callback props if provided, otherwise use internal handlers
  const executeAction = onAction ?? handlePlay;
  const executeInfo = onInfo ?? handleMoreInfo;

  return (
    <div
      ref={ref}
      aria-label={`${title.name} - Continue watching - ${progress.progressPercent}% complete`}
      className={`
                group flex-shrink-0 self-start text-left w-[calc(100vw-3rem)] sm:w-[500px] space-y-3 snap-center
                transition-all duration-500 ease-out
                ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}
                ${className}
            `}
    >
      {/* Landscape poster with 16:9 aspect ratio - progress pulse hover effect */}
      <div className="card-base card-hover-horizontal aspect-video">
        {/* NEW badge for recently added titles */}
        {title.createdAt ? <NewBadge createdAt={title.createdAt} /> : null}

        {/* Backdrop/poster image - prioritize horizontal tile for landscape display */}
        <img
          src={title.horizontalTileUrl ?? title.backdropUrlHd ?? title.posterUrlHd ?? title.posterUrl ?? undefined}
          alt={title.name}
          className="h-full w-full object-cover"
        />

        {/* Progress bar overlay - pulses on hover */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-neutral-800 z-20 transition-all duration-300 group-hover:h-1.5 group-[.rail-focused]:h-1.5">
          <div
            className="h-full bg-red-600 transition-all duration-300 group-hover:brightness-125 group-hover:saturate-150 group-[.rail-focused]:brightness-125 group-[.rail-focused]:saturate-150"
            style={{ width: `${progress.progressPercent}%` }}
            aria-label={`${progress.progressPercent}% watched`}
          />
        </div>

        {/* Gradient overlay on hover */}
        <div className="card-gradient-light" />
        <div className="card-gradient-dark" />
        {/* Play button overlay - shown on hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 group-[.rail-focused]:opacity-100 transition-opacity duration-300 z-10 pointer-events-none">
          <Button variant="ghost-sm" className="pointer-events-auto" onClick={executeAction}>
            <Play size={20} />
            <span className="ml-2 text-base font-semibold">Resume</span>
          </Button>
        </div>
      </div>

      {/* Metadata below the card */}
      <div className="px-1 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 space-y-2">
            <h2 className="font-medium text-white text-base leading-tight line-clamp-2">{title.name}</h2>

            <div className="space-y-1">
              {/* Year, Runtime, and Genres */}
              <div className="flex items-center gap-2 text-xs text-neutral-400 min-w-0">
                {title.year && <span className="flex-shrink-0">{title.year}</span>}
                {title.year && title.runtimeMinutes && <span className="flex-shrink-0">•</span>}
                {title.runtimeMinutes && <span className="flex-shrink-0">{title.runtimeMinutes} min</span>}
                {(title.year || title.runtimeMinutes) && title.genres && title.genres.length > 0 && (
                  <span className="flex-shrink-0">•</span>
                )}
                {title.genres && title.genres.length > 0 && (
                  <span className="truncate">{title.genres.slice(0, 2).join(", ")}</span>
                )}
              </div>

              {/* Director */}
              {title.directorName && (
                <p className="text-xs text-neutral-500">
                  Dir. <span className="text-neutral-400 font-medium">{title.directorName}</span>
                </p>
              )}
            </div>
          </div>

          <div className="pointer-events-none">
            <Button variant="ghost-sm" className="pl-0 flex-shrink-0 -mt-1 pointer-events-auto" onClick={executeInfo}>
              <Info size={16} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
